import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  getFontFamily,
  showModalPortalGuest,
  showModalReprocessKyc,
  showSnackbar,
  useHistory,
} from 'utils'
import {WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {ForkygramFollowCallbackType} from 'types'
import {
  Button,
  ButtonOutline,
  Image,
  Paragraph,
  IconVerifiedBadge,
  ConfirmationModal,
  Icon,
  ParsedText,
} from 'common/components'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import {TemplateFollowModalItemProps} from './TemplateFollowModalItemProps'

const StyledListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${convertUnit(12)} ${convertUnit(20)};
  margin: 0;
`

const StyledParagraph = styled(Paragraph)`
  font-size: ${convertUnit(16)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledButton = styled(Button)`
  height: ${convertUnit(28)};
  padding: ${convertUnit(4)} ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: fit-content;
  }
`

const StyledAvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${convertUnit(36)};
`

const StyledAvatar = styled(Image)`
  border-radius: 50%;
  width: ${convertUnit(36)};
  height: ${convertUnit(36)};
  object-fit: cover;
  cursor: pointer;
  flex: none;
`

const StyledInfoWrapper = styled.div`
  display: flex;
  gap: ${convertUnit(10)};
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledButtonOutline = styled(ButtonOutline)`
  height: ${convertUnit(28)};
  padding: ${convertUnit(4)} ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: fit-content;
  }
`

const StyledIconVerifiedBadge = styled(IconVerifiedBadge)`
  display: inline-block;
  margin-right: ${convertUnit(4)};
  line-height: ${convertUnit(16)};
`

const StyledRightActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(8)};
  align-items: center;
`

const StyledIconContainer = styled.div`
  cursor: pointer;
`

const StyledModalText = styled(Paragraph)`
  text-align: center;
`
export default function TemplateFollowModalItem({
  data,
  callback,
  onClose,
  isFollower,
  onRemoveFollower,
}: TemplateFollowModalItemProps) {
  const {translate} = useTranslation()
  const [isFollowed, setIsFollowed] = useState<boolean>(data.is_followed)
  const [isRequested, setIsRequested] = useState(data.is_follow_request)
  const history = useHistory()
  const {access_token, id: userId} = useSelector('user') || {}
  const {kycStatus} = useSelector('yuserActivationState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const [modal, setModal] = useState<ForkygramFollowCallbackType>()

  const handleFeatureDisabled = useCallback(() => {
    onClose()
    if (kycStatus === 'onhold') {
      if (similarStatus === 'CHOOSE_SELF') {
        return showModalReprocessKyc()
      }
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [history, kycStatus, onClose, similarStatus])

  const handleRemoveFollower = useCallback(() => {
    requestData('giftshop_delete_follower_request', {
      data: {member_id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          showSnackbar(translate('forkygram:removeFollowerSuccess'))
          onRemoveFollower(data)
          callback('remove-follower')
        } else {
          showSnackbar(
            translate('global:messageError', {context: `code-${status}`}),
          )
        }
      },
    })
  }, [callback, data, onRemoveFollower, translate])

  const handleUnfollow = useCallback(() => {
    setIsFollowed(false)
    setIsRequested(false)
    !data.is_private && callback('unfollow')
    requestData('giftshop_delete_follow', {
      data: {member_id: data.id},
      onRequestFailed: () => {
        setIsFollowed(true)
        data.is_follow_request && setIsRequested(true)
        !data.is_private && callback('follow')
      },
    })
  }, [callback, data.id, data.is_follow_request, data.is_private])

  const handleFollow = useCallback(() => {
    data.is_private ? setIsRequested(true) : setIsFollowed(true)
    !data.is_private && callback('follow')
    requestData('giftshop_post_follow', {
      data: {member_id: data.id},
      onRequestFailed: () => {
        setIsFollowed(false)
        callback('unfollow')
      },
    })
  }, [callback, data.id, data.is_private])

  const goToProfile = useCallback(() => {
    history.push(
      'giftshop_profile',
      {
        self: data.id === userId,
        postData: {
          member_id: data.id,
          username: data.username,
          photo: data.photo,
        },
      },
      data.username,
    )

    onClose()
  }, [history, data.id, data.username, data.photo, userId, onClose])

  const handleClickFollow = useCallback(() => {
    if (access_token) {
      if (!(kycStatus === 'verified' || kycStatus === 'verified_check')) {
        handleFeatureDisabled()
        return
      }
      handleFollow()
    } else {
      onClose()
      showModalPortalGuest()
    }
  }, [access_token, handleFeatureDisabled, handleFollow, kycStatus, onClose])

  const handleRenderUnfollowModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('forkygram:unfollowTitle')}
        text={translate('forkygram:unfollowDescription')}
        toggleModal={() => setModal(undefined)}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:unfollow')}
        onConfirm={handleUnfollow}
      />
    ),
    [handleUnfollow, translate],
  )

  const handleRenderRemoveFollowerModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('forkygram:removeFollowerTitle')}
        text={
          <StyledModalText fontSize="l" lineHeight={31}>
            <ParsedText
              parse={[
                {
                  pattern: /<username>.*?<\/username>/,
                  style: {
                    fontFamily: getFontFamily('bold'),
                  },
                  renderText: (str) =>
                    str.replace(/<username>|<\/username>/g, ''),
                },
              ]}>
              {translate('forkygram:removeFollowerDescription', {
                username: data.username,
              })}
            </ParsedText>
          </StyledModalText>
        }
        toggleModal={() => setModal(undefined)}
        cancelText={translate('global:cancel')}
        confirmText={translate('forkygram:removeFollower')}
        onConfirm={handleRemoveFollower}
      />
    ),
    [data.username, handleRemoveFollower, translate],
  )

  const handleRenderModal = useMemo(() => {
    switch (modal) {
      case 'unfollow':
        return handleRenderUnfollowModal
      case 'remove-follower':
        return handleRenderRemoveFollowerModal
    }
    return <></>
  }, [handleRenderRemoveFollowerModal, handleRenderUnfollowModal, modal])

  return (
    <StyledListItem>
      {handleRenderModal}
      <StyledInfoWrapper
        onClick={() => (data.is_banned ? undefined : goToProfile())}>
        <StyledAvatarContainer>
          <StyledAvatar src={data.photo} alt={data.username} />
        </StyledAvatarContainer>
        <StyledParagraph
          fontWeight="medium"
          className={data.is_banned ? undefined : 'pressable'}>
          {data.username}
        </StyledParagraph>
        {data.creator_status === 'verified' && <StyledIconVerifiedBadge />}
      </StyledInfoWrapper>
      <StyledRightActionContainer>
        {!data.is_banned &&
          data.id !== userId &&
          (isFollowed || isRequested ? (
            <StyledButtonOutline
              isLoading={false}
              borderColor="gray_5"
              color="gray_6"
              label={translate(
                isFollowed ? 'forkygram:following' : 'global:requested',
              )}
              onClick={() => {
                if (
                  !(kycStatus === 'verified' || kycStatus === 'verified_check')
                ) {
                  handleFeatureDisabled()
                  return
                }
                isFollowed && data.is_private
                  ? setModal('unfollow')
                  : handleUnfollow()
              }}
            />
          ) : (
            <StyledButton
              isLoading={false}
              label={translate('forkygram:follow')}
              onClick={handleClickFollow}
            />
          ))}
        {isFollower ? (
          <StyledIconContainer onClick={() => setModal('remove-follower')}>
            <Icon type="close" color="gray_5" />
          </StyledIconContainer>
        ) : (
          <></>
        )}
      </StyledRightActionContainer>
    </StyledListItem>
  )
}
